import React, { useState, useEffect, useRef } from "react";
import Seo from "./Seo";
import gurgaonImg from "../assets/Gurgaon_image.jpeg";
import logoImg from "../assets/Logo.jpeg";
import noidaImg from "../assets/Noida.png";
import { Link } from "react-router-dom";
import { useAudio } from "../components/AudioContext.js";
import '../styles/HeroSection.css';

function renderEmphasis(text) {
  return text.split(/\*(.+?)\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

function stripEmphasis(text) {
  return text.replace(/\*(.+?)\*/g, '$1');
}

function faqAnswerText(faq) {
  const bullets = (faq.bullets || []).map(b => `${b.label}: ${b.text}`).join(' ');
  return stripEmphasis(bullets ? `${faq.answer} ${bullets}` : faq.answer);
}

export default function HeroSection() {
  const { startAudio } = useAudio();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [reviewsPaused, setReviewsPaused] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const gameTextRefs = useRef([]);
  const gameTitleRef = useRef(null);
  const locationsTitleRef = useRef(null);
  const locationCardsRef = useRef([]);
  const faqTitleRef = useRef(null);
  const faqItemsRef = useRef([]);
  const reviewsTitleRef = useRef(null);

  useEffect(() => {
    startAudio();
  }, [startAudio]);

  // Scroll-aware nav
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '-30px 0px -30px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          entry.target.classList.remove('animate-out');
        } else {
          entry.target.classList.add('animate-out');
          entry.target.classList.remove('animate-in');
        }
      });
    }, observerOptions);

    const elementsToObserve = [
      reviewsTitleRef.current,
      gameTitleRef.current,
      ...gameTextRefs.current,
      locationsTitleRef.current,
      ...locationCardsRef.current,
      faqTitleRef.current,
      ...faqItemsRef.current
    ].filter(Boolean);

    elementsToObserve.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  const navItems = [
    { name: "Home", href: "#home" },
    { name: "The Game", href: "#about-game" },
    { name: "Locations", href: "#locations" },
    { name: "FAQs", href: "#faqs" },
    { name: "Blog", href: "/blog" },
    { name: "The Architect", href: "/creator" }
  ];

  const gameTextLines = [
    "Check in with friends like any normal stay",
    "But the unexpected can strike anytime, anywhere",
    "Become part of an interactive open-world mystery",
    "Figure real-life hints and facts to solve the case",
    "Your actions decide how the mystery unfolds",
    "Can your group solve it before time runs out?"
  ];

  const tripAdvisorReviews = [
    {
      name: "Travel51990279153",
      location: "",
      date: "Mar 2026",
      rating: 5,
      title: "Such a niche experience!",
      review: "We booked this experience for one of my cousins' birthday. This was the best ever birthday party that I ever attended. Generally birthday parties tend to get boring, but this game helped keep it completely engaging and the adrenaline rush was the highlight! Working as one team to solve a unique story- YES YES YES! The game was the perfect addition to make the party more special.",
      avatar: "T"
    },
    {
      name: "Deepak",
      location: "Gurugram (Gurgaon), India",
      date: "Apr 2026",
      rating: 5,
      title: "From Party to Mission Mode",
      review: "If you're looking for a perfect getaway with a touch of mystery, this place is a must-visit. We came to celebrate a friend's birthday, and what started as a simple gathering turned into an unforgettable experience. A mystery game began during our stay. At first, we took it lightly, but as the story unfolded, it became super thrilling and engaging, it actually felt like we were on a mission. Nothing was predictable, and the twists kept us hooked throughout. We eventually solved it, but none of us saw the ending coming. The feeling at the end was amazing. Would definitely recommend if you're looking for a mix of celebration, adventure, and something unique.",
      avatar: "D"
    },
    {
      name: "Mobile34455179683",
      location: "",
      date: "Apr 2026",
      rating: 5,
      title: "An Amazing Stay with a Real Mystery Twist",
      review: "Had an amazing experience. The place was beautiful. It was such a unique stay with friends, and the mystery we tackled felt like a real deal, super engaging and fun. Would definitely recommend 😀",
      avatar: "M"
    },
    {
      name: "Akash A",
      location: "",
      date: "Feb 2026",
      rating: 5,
      title: "Immersive experience with a luxury stay",
      review: "Excellent way to spend weekend with friends. The experience was so immersive. One minute we were settling in and the next we were caught up in a mystery. Everyone was so invested that time literally flew away. The clues, the suspense, unexpected twists, this mystery mansion had everything to keep us hooked up. Way more exciting than usual plans and such a cool experience with a luxury stay at night.",
      avatar: "AA"
    },
    {
      name: "Pankaj B",
      location: "",
      date: "Nov 2025",
      rating: 5,
      title: "Mystery cracked",
      review: "Our visit to Mystery Mansion was an absolutely amazing experience from start to finish. The ambience, music, and overall setup were incredibly immersive and thoughtfully designed. From the moment we entered, it felt like stepping into a completely different world filled with suspense and excitement. What made it even more enjoyable was the constant feeling of curiosity and anticipation. Unexpected and interesting things kept happening throughout, making us eager to figure out what would happen next. The experience kept us engaged the entire time and made it both thrilling and fun.",
      avatar: "PB"
    }
  ];

  /*
   * Ordered broad -> specific: what the experience is, then where and how long,
   * then who it suits, then the occasion-specific and booking questions.
   *
   * Text between *asterisks* renders bold (see renderEmphasis) and is stripped
   * for the FAQ schema. `bullets` is optional. `whatsapp: true` turns the phone
   * number in that answer into a wa.me link — it used to be keyed off the array
   * index, which silently broke whenever the list was reordered.
   */
  const faqData = [
    {
      question: "What is the game about?",
      answer: "Check in with your group for a party like any other… until the unexpected happens! Imagine an unthinkable mystery unfolding right before your eyes as you stay at the property. As the eye witnesses, of course you need to crack it"
    },
    {
      question: "How is Mystery Mansion different from an escape room?",
      answer: "Unlike an escape room, Mystery Mansion is an *open-world mystery experience spread across an entire villa and several hours*. You aren't locked inside a room solving a sequence of puzzles. The mystery unfolds around you through clues, characters, phones, the internet and unexpected events, while you are free to explore the property and investigate the story in your own way."
    },
    {
      question: "Where are Mystery Mansions located?",
      answer: "Currently, we have Mystery Mansions in Gurugram and Greater Noida. More locations are launching soon."
    },
    {
      question: "How far is Mystery Mansion from Delhi?",
      answer: "Mystery Mansion currently has locations in *Gurugram and Greater Noida, both within Delhi NCR*. Travel time depends on the location you choose and where in Delhi you are travelling from. You can check the exact location and travel time on Google Maps before booking."
    },
    {
      question: "How long does the game last?",
      answer: "The game typically lasts 3–5 hours, but the timing can vary depending on your group's detective skills."
    },
    {
      question: "How many people can play Mystery Mansion?",
      answer: "The Mystery Mansion gaming experience is best suited for *8–12 active players*, but we are happy to host groups of *15–20 guests* as well. For larger groups, please keep two things in mind:",
      bullets: [
        {
          label: "Accommodation",
          text: "Our properties have 4–5 bedrooms, so larger groups should be comfortable managing within the available sleeping arrangements."
        },
        {
          label: "Gaming experience",
          text: "The mystery experience is best with 8–12 actively involved players. Larger groups work well when some guests prefer to enjoy the villa and celebration without being as actively involved in solving the mystery."
        }
      ]
    },
    {
      question: "Is accommodation included?",
      answer: "Yes. Mystery Mansion combines the immersive mystery experience with a *private villa stay*. Accommodation is part of the experience, allowing your group to enjoy the property before, during and after the main mystery."
    },
    {
      question: "What is the stay like?",
      answer: "Stays are in top-rated, premium villas—each carefully chosen for comfort, privacy, and party vibes. You can enjoy indoor games, swimming pools, pre-booked dining, and the freedom to bring your own drinks. It's your space for the night."
    },
    {
      question: "Is it suitable for all ages?",
      answer: "Yes! But we recommend players aged 10 and above, as the mysteries deepen as the game progresses."
    },
    {
      question: "What language is the game in?",
      answer: "Currently the game is available in Hindi, to keep the experience authentic and true to the natural dynamics of the situation."
    },
    {
      question: "Is Mystery Mansion scary?",
      answer: "Mystery Mansion is primarily a mystery and investigation experience, not a horror house. It includes suspense, unexpected events and an immersive atmosphere, but the objective is to make you feel like you're inside a mystery story rather than simply trying to scare you. We don't disclose anything more about the story in advance — discovering what happens is part of the experience."
    },
    {
      question: "Will someone guide us through the game?",
      answer: "The experience is immersive and realistic—no guides. Only your wits and God (we can play God!) can help you."
    },
    {
      question: "Are the stories the same at every mansion?",
      answer: "Every Mystery Mansion has its own unique story, inspired by the location, history, and ambiance of the property. Each visit promises a fresh adventure."
    },
    {
      question: "How much does Mystery Mansion cost?",
      answer: "Pricing depends on the *location, date and group requirements*. Contact us with your preferred location, date and group size, and we'll share the applicable price and availability."
    },
    {
      question: "Do I need to book in advance?",
      answer: "Yes! Our premium properties often get fully booked 1–2 months in advance on other platforms. For availability, reach out to us on WhatsApp at +91 8279845322—you might get lucky!",
      whatsapp: true
    },
    {
      question: "Can we drink during the experience?",
      answer: "Yes. *Guests of legal drinking age can consume alcohol on a BYOB basis.* We recommend drinking responsibly so everyone can fully participate in and enjoy the mystery."
    },
    {
      question: "Can Mystery Mansion be booked for birthdays?",
      answer: "Yes. Mystery Mansion is ideal for *private birthday celebrations where the party and mystery unfold together*. Your group gets the villa to itself to celebrate, while the mystery becomes part of the experience throughout your stay. We recommend that the person making the booking discusses their celebration plans with us in advance so we can help ensure everything works smoothly alongside the mystery."
    },
    {
      question: "Is Mystery Mansion suitable for bachelor/bachelorette parties?",
      answer: "Yes. Mystery Mansion works particularly well for bachelor and bachelorette groups looking for something beyond a conventional party. Your group gets a private villa stay combined with an immersive mystery that you solve together *while the party continues around it*."
    },
    {
      question: "Can companies book Mystery Mansion for team outings?",
      answer: "Yes. Companies can book Mystery Mansion for *private team outings and offsites for groups of up to 20 people*. The experience naturally involves investigation, discussion, collaboration and collective decision-making, making it an engaging group activity alongside the private villa stay."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "EntertainmentBusiness",
    "name": "Mystery Mansion",
    "url": "https://mysterymansion.in",
    "logo": "https://mysterymansion.in/assets/Logo.jpeg",
    "description": "Mystery Mansion is an immersive open-world mystery experience combined with a luxury villa stay. Currently available in Gurugram and Greater Noida.",
    "telephone": "+918279845322",
    "sameAs": [
      "https://www.instagram.com/mysterymansion.in"
    ],
    "address": [
      {
        "@type": "PostalAddress",
        "addressLocality": "Gurugram",
        "addressRegion": "Haryana",
        "addressCountry": "IN"
      },
      {
        "@type": "PostalAddress",
        "addressLocality": "Greater Noida",
        "addressRegion": "Uttar Pradesh",
        "addressCountry": "IN"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "bestRating": "5",
      "reviewCount": String(tripAdvisorReviews.length)
    },
    "review": tripAdvisorReviews.map((r) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "datePublished": r.date,
      "reviewBody": r.review,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": String(r.rating),
        "bestRating": "5"
      }
    }))
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faqAnswerText(f)
      }
    }))
  };

  return (
    <div className="page-hero">
      <Seo
        path="/"
        title="Mystery Mansion | Immersive Mystery Staycation in Delhi NCR"
        description="Experience a real-life open-world mystery game during a private villa staycation in Delhi NCR. Perfect for groups, birthdays and unique celebrations."
      >
        <script type="application/ld+json">{JSON.stringify(businessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Seo>

      {/* ===== NAVIGATION BAR ===== */}
      <nav className={`fixed top-0 left-0 right-0 z-50 mm-nav ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 md:h-18">
            <div className="flex-shrink-0 mm-insta-btn">
              <a href="https://www.instagram.com/mysterymansion.in?igsh=aXZhenZydmFoYjR4"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="inline-flex items-center gap-2"
                style={{ textDecoration: 'none' }}
              >
                <div style={{
                  background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  flexShrink: 0,
                  position: 'relative'
                }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      width: '20px',
                      height: '20px',
                      display: 'block',
                      position: 'absolute',
                      top: '50%',
                      left: 'calc(50% - 0.6px)',
                      transform: 'translate(-50%, -50%)'
                    }}
                    viewBox="0 0 24 24"
                    fill="white"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </div>
                <span style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.8rem',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap'
                }}>Gurugram | Greater Noida</span>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-2">
                {navItems.map((item, index) => (
                  item.href.startsWith('#') ? (
                    <a
                      key={index}
                      href={item.href}
                      className="mm-nav-link"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={index}
                      to={item.href}
                      className="mm-nav-link"
                    >
                      {item.name}
                    </Link>
                  )
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="focus:outline-none p-2"
                aria-label="Toggle menu"
                style={{ color: 'var(--text-secondary)' }}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <div className={`md:hidden mm-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item, index) => (
                item.href.startsWith('#') ? (
                  <a
                    key={index}
                    href={item.href}
                    className="mm-nav-link block py-3"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={index}
                    to={item.href}
                    className="mm-nav-link block py-3"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* ===== HERO SECTION ===== */}
      <section id="home" className="mm-hero">
        {/* Atmospheric fog orbs */}
        <div className="mm-fog">
          <div className="mm-fog-orb"></div>
          <div className="mm-fog-orb"></div>
          <div className="mm-fog-orb"></div>
        </div>

        <div className="relative min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-44 sm:pb-40 mm-hero-content">
          <div className="w-full max-w-4xl space-y-4 sm:space-y-5 md:space-y-6">
            <p className="mm-subtitle text-center">
              World's Only
            </p>

            <h1 className="text-center px-2 my-0">
              <img
                src={logoImg}
                alt="Mystery Mansion Logo"
                className="mx-auto block h-28 sm:h-36 md:h-48 lg:h-56 my-0"
                style={{
                  maxWidth: '100%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 0 40px rgba(201, 168, 76, 0.15))'
                }}
              />
            </h1>

            <p className="mm-tagline text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-center px-2">
              {"One group. One mansion. One night.".split('').map((letter, index) => (
                <span key={index} className="wavy-letter">
                  {letter === ' ' ? '\u00A0' : letter}
                </span>
              ))}
            </p>

            <div className="flex justify-center pt-6 sm:pt-8 md:pt-12">
              <a href="#locations">
                <button className="mm-cta">
                  Book Now
                </button>
              </a>
            </div>
            {/* Scroll Indicator */}
            <div className="mm-scroll-indicator hidden sm:flex">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--gold-dim)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
              </svg>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 mm-hero-bar py-3 sm:py-4 md:py-5 px-4 z-40">
            <div className="max-w-7xl mx-auto text-center space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm md:text-base lg:text-lg px-2"
                style={{ fontFamily: 'var(--font-accent)', color: 'var(--text-secondary)', letterSpacing: '0.05em', fontWeight: 400 }}>
                Stay in a premium villa with a group of friends or colleagues
              </p>
              <div className="w-32 sm:w-48 md:w-64 lg:w-80 mx-auto mm-hero-bar-divider"></div>
              <p className="text-xs sm:text-sm md:text-base lg:text-lg px-2"
                style={{ fontFamily: 'var(--font-accent)', color: 'var(--text-secondary)', letterSpacing: '0.05em', fontWeight: 400 }}>
                Solve a real-life mystery game during the stay
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider"></div>

      <div className="section-divider"></div>

      {/* ===== ABOUT THE GAME ===== */}
      <section id="about-game" className="mm-about">
        <div className="min-h-screen flex flex-col items-center justify-center px-4 py-20 sm:py-24">
          <div className="relative z-10 w-full text-center max-w-6xl mx-auto">
            <h1
              ref={gameTitleRef}
              className="scroll-animate-up text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6"
              style={{ fontFamily: "var(--font-heading)", color: 'var(--text-primary)' }}
            >
              {"About the Game".split('').map((letter, index) => (
                <span key={index} className="wavy-letter">
                  {letter === ' ' ? '\u00A0' : letter}
                </span>
              ))}
            </h1>

            <div className="mm-gold-divider"></div>

            <div className="mm-journey mt-8 sm:mt-12">
              {gameTextLines.slice(0, -1).map((line, index) => (
                <div
                  key={index}
                  ref={el => gameTextRefs.current[index] = el}
                  className="scroll-animate mm-journey-step"
                >
                  <span className="mm-journey-num" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <p className="mm-journey-text">{line}</p>
                </div>
              ))}

              <div
                ref={el => gameTextRefs.current[gameTextLines.length - 1] = el}
                className="scroll-animate mm-journey-finale"
              >
                <p>{gameTextLines[gameTextLines.length - 1]}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider"></div>

      {/* ===== LOCATIONS ===== */}
      <section id="locations" className="mm-locations">
        <div className="min-h-screen flex flex-col items-center justify-center py-16 sm:py-20 px-4">
          <h1
            ref={locationsTitleRef}
            className="scroll-animate-up text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-10 sm:mb-16 text-center relative z-10"
            style={{ fontFamily: "var(--font-heading)", color: 'var(--text-primary)' }}
          >
            {"Locations".split('').map((letter, index) => (
              <span key={index} className="wavy-letter">
                {letter === ' ' ? '\u00A0' : letter}
              </span>
            ))}
          </h1>

          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-6 sm:gap-8 max-w-6xl mx-auto relative z-10 w-full">
            {/* Gurugram Card */}
            <div
              ref={el => locationCardsRef.current[0] = el}
              className="scroll-animate-scale mm-location-card w-full sm:w-[calc(50%-1rem)] max-w-[400px] mx-auto"
            >
              <div className="mm-location-img-wrap">
                <img
                  src={gurgaonImg}
                  alt="Gurgaon"
                  className="w-full h-48 sm:h-64 md:h-80 object-cover rounded-lg"
                  style={{ objectPosition: '42% center' }}
                />
              </div>
              <div className="p-4 sm:p-6">
                <h2 className="mm-card-title text-xl sm:text-2xl md:text-3xl mb-4 sm:mb-6">
                  {"Gurugram".split('').map((letter, index) => (
                    <span key={index} className="wavy-letter">
                      {letter === ' ' ? '\u00A0' : letter}
                    </span>
                  ))}
                </h2>

                <Link to="/gurugram">
                  <button className="mm-cta text-sm w-full sm:w-auto">
                    Book Now
                  </button>
                </Link>
              </div>
            </div>

            {/* Greater Noida Card */}
            <div
              ref={el => locationCardsRef.current[1] = el}
              className="scroll-animate-scale mm-location-card w-full sm:w-[calc(50%-1rem)] max-w-[400px] mx-auto"
            >
              <div className="mm-location-img-wrap">
                <img
                  src={noidaImg}
                  alt="Greater Noida"
                  className="w-full h-48 sm:h-64 md:h-80 object-cover rounded-lg"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h2 className="mm-card-title text-xl sm:text-2xl md:text-3xl mb-4 sm:mb-6">
                  {"Greater Noida".split('').map((letter, index) => (
                    <span key={index} className="wavy-letter">
                      {letter === ' ' ? '\u00A0' : letter}
                    </span>
                  ))}
                </h2>

                <Link to="/greater-noida">
                  <button className="mm-cta text-sm w-full sm:w-auto">
                    Book Now
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider"></div>

      {/* ===== FAQS ===== */}
      <section id="faqs" className="mm-faqs">
        <div className="min-h-screen flex flex-col items-center justify-center py-16 sm:py-20 px-4">
          <h1
            ref={faqTitleRef}
            className="scroll-animate-up text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-10 sm:mb-16 text-center relative z-10 px-2"
            style={{ fontFamily: "var(--font-heading)", color: 'var(--text-primary)' }}
          >
            {"Frequently Asked Questions".split('').map((letter, index) => (
              <span key={index} className="wavy-letter">
                {letter === ' ' ? '\u00A0' : letter}
              </span>
            ))}
          </h1>

          <div className="w-full max-w-4xl mx-auto relative z-10">
            {faqData.map((faq, index) => (
              <div
                key={index}
                ref={el => faqItemsRef.current[index] = el}
                className="scroll-animate mm-faq-item"
              >
                <div
                  className="mm-faq-question"
                  onClick={() => toggleFAQ(index)}
                >
                  <span className="text-xs sm:text-sm md:text-base lg:text-lg pr-2">
                    {faq.question}
                  </span>
                  <svg
                    className={`mm-faq-icon w-5 h-5 sm:w-6 sm:h-6 ${openFAQ === index ? 'rotate' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <div
                  className={`mm-faq-answer ${openFAQ === index ? 'open' : ''}`}
                >
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg">
                    {faq.whatsapp ? (
                      <>
                        Yes! Our premium properties often get fully booked 1–2 months in advance on other platforms. For availability, reach out to us on WhatsApp at{' '}
                        <a href="https://wa.me/918279845322" className="mm-whatsapp-link">
                          +91 8279845322
                        </a>
                        —you might get lucky!
                      </>
                    ) : (
                      renderEmphasis(faq.answer)
                    )}
                  </p>
                  {faq.bullets && (
                    <ul className="mm-faq-list text-xs sm:text-sm md:text-base lg:text-lg">
                      {faq.bullets.map((bullet, i) => (
                        <li key={i}>
                          <strong>{bullet.label}:</strong> {bullet.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 sm:mt-16 text-center relative z-10 px-4">
            <p
              className="text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-6"
              style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}
            >
              Still have questions?
            </p>
            <a
              href="https://wa.me/918279845322"
              target="_blank"
              rel="noopener noreferrer"
              className="mm-cta inline-flex items-center justify-center gap-2 text-sm sm:text-base"
              style={{
                background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.15) 0%, rgba(37, 211, 102, 0.05) 100%)',
                borderColor: '#25D366',
                color: '#6ee7a0'
              }}
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.215" />
              </svg>
              <span className="whitespace-nowrap">Contact us on WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      <div className="section-divider"></div>

      {/* ===== TRIPADVISOR REVIEWS ===== */}
      <section id="reviews" className="mm-reviews">
        <div className="py-16 sm:py-20 md:py-24 px-4">
          <div className="mm-reviews-header">
            <div
              ref={reviewsTitleRef}
              className="scroll-animate-up"
            >
              <div className="mm-ta-badge">
                <svg className="mm-ta-logo" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="16" fill="#34E0A1" opacity="0.15"/>
                  <path d="M16 6C10.477 6 6 10.477 6 16s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6zm0 2.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zm-4.5 5a2 2 0 100 4 2 2 0 000-4zm9 0a2 2 0 100 4 2 2 0 000-4zM16 19c-2.5 0-4.5 1.5-4.5 3.5h9c0-2-2-3.5-4.5-3.5z" fill="#34E0A1"/>
                </svg>
                <span className="mm-ta-text">Tripadvisor Reviews</span>
              </div>

              <h2
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2"
                style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}
              >
                {"What Our Guests Say".split('').map((letter, index) => (
                  <span key={index} className="wavy-letter">
                    {letter === ' ' ? '\u00A0' : letter}
                  </span>
                ))}
              </h2>
            </div>
          </div>

          <div className="mm-reviews-track-wrapper mt-10 sm:mt-14">
            <div className={`mm-reviews-track ${reviewsPaused ? 'paused' : ''}`}>
              {/* Duplicate reviews for seamless infinite scroll */}
              {[...tripAdvisorReviews, ...tripAdvisorReviews].map((review, index) => (
                <div
                  key={index}
                  className="mm-review-card"
                  onMouseEnter={() => setReviewsPaused(true)}
                  onMouseLeave={() => setReviewsPaused(false)}
                >
                  <span className="mm-review-quote">"</span>
                  <div className="mm-review-header">
                    <div className="mm-review-avatar">{review.avatar}</div>
                    <div className="mm-review-author">
                      <div className="mm-review-name">{review.name}</div>
                      <div className="mm-review-location">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        {review.location}
                      </div>
                    </div>
                  </div>

                  <div className="mm-review-meta">
                    <div className="mm-review-stars">
                      {Array.from({length: review.rating}).map((_, i) => (
                        <svg key={i} className="mm-review-star" viewBox="0 0 24 24" fill="#34E0A1">
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                      ))}
                      {Array.from({length: 5 - review.rating}).map((_, i) => (
                        <svg key={`empty-${i}`} className="mm-review-star" viewBox="0 0 24 24" fill="rgba(52, 224, 161, 0.2)">
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                      ))}
                    </div>
                    <span className="mm-review-date">{review.date}</span>
                  </div>

                  <div className="mm-review-title">{review.title}</div>
                  <div className="mm-review-text">{review.review}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center relative z-10">
            <a
              href="https://www.tripadvisor.in/Attraction_Review-g297615-d34062500-Reviews-Mystery_Mansion-Gurugram_Gurgaon_Gurgaon_District_Haryana.html"
              target="_blank"
              rel="noopener noreferrer"
              className="mm-ta-cta"
            >
              Read more on Tripadvisor
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}